<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" :position="dialogPosition">
    <q-card>
      <ModalHeader header="Give Feedback" @on-close="onDialogHide" />

      <q-form ref="feedbackForm" @submit="submitFeedback" :disable="isLoading">
        <q-card-section style="padding-top: 0">
          <p>
            Use the form below to send us your comments. We read all feedback carefully and may
            contact you to better understand the comments you submitted.
          </p>

          <label style="font-weight: bold; display: block; margin-bottom: 8px">
            Feedback Type <span style="color: red">*</span>
          </label>
          <q-select
            v-model="feedbackType"
            :options="feedbackOptions"
            outlined
            dense
            style="margin-bottom: 24px"
          />

          <label style="font-weight: bold; display: block; margin-bottom: 8px">
            Phone Number
          </label>
          <vue-tel-input
            v-model="phoneNumber"
            valid-characters-only
            auto-format
            class="rounded-borders"
            default-country="us"
            :preferred-countries="['us', 'ca']"
            :style-classes="['border-none', 'full-width']"
            :dropdown-options="{ showSearchBox: true, width: '100%', showFlags: true }"
            style="margin-bottom: 24px"
          >
            <template v-slot:arrow-icon>
              <q-icon name="arrow_drop_down" />
            </template>
          </vue-tel-input>

          <label style="font-weight: bold; display: block; margin-bottom: 8px">
            Comments <span style="color: red">*</span>
          </label>
          <q-input
            v-model="comments"
            placeholder="I want to let you know that ..."
            type="textarea"
            outlined
            dense
            lazy-rules
            :rules="[(val) => val.trim() !== '' || 'Provide a short description of the feedback']"
          />
        </q-card-section>

        <hr style="border: none; border-top: 1px solid #d3d3d3; width: 100%; margin: 12px 0" />

        <q-card-actions align="right" class="q-pa-md" style="padding-top: 0">
          <q-btn
            class="rounded-borders"
            flat
            label="Cancel"
            @click="closeDialog"
            style="margin-right: 8px"
          />
          <PrimaryButton
            label="Submit Feedback"
            color="primary"
            type="submit"
            :loading="isLoading"
          />
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { ref, useTemplateRef } from 'vue';
import { useStore } from 'vuex';
import { useDialogPluginComponent } from 'quasar';
import { SendSuccess, SendError } from '@/helpers.js';
import { VueTelInput } from 'vue-tel-input';
import 'vue-tel-input/vue-tel-input.css';

import { Sentry } from '@/vendors/Sentry';
import ModalHeader from '@/components/modals/ModalHeader.vue';
import { useMobile } from '@/hooks/useMobile';
import PrimaryButton from '@/components/brand/PrimaryButton.vue';

const store = useStore();

const feedbackForm = useTemplateRef('feedbackForm');
const feedbackOptions = ['Bug Report', 'Feature Request', 'General Feedback'];
const feedbackType = ref(feedbackOptions[0]);
const phoneNumber = ref('');
const comments = ref('');
const isLoading = ref(false);

const { dialogPosition } = useMobile();
const { dialogRef, onDialogHide } = useDialogPluginComponent();

const closeDialog = () => {
  onDialogHide();
};

const resetForm = () => {
  feedbackType.value = feedbackOptions[0];
  phoneNumber.value = '';
  comments.value = '';
};

const submitFeedback = async () => {
  if (!(await feedbackForm.value.validate())) {
    return;
  }
  isLoading.value = true;
  try {
    const data = {
      feedbackType: feedbackType.value,
      phoneNumber: phoneNumber.value,
      comments: comments.value,
    };
    await store.dispatch('sendFeedback', data);
    SendSuccess('Feedback submitted successfully!');
    resetForm();
    closeDialog();
  } catch (error) {
    Sentry.captureException(error);
    SendError('Failed to submit feedback.');
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped lang="scss">
:deep(.vue-tel-input) {
  height: 40px;
  border-radius: $generic-border-radius;
  border: 1px solid $grey-5;
  transition: border-color 0.36s cubic-bezier(0.4, 0, 0.2, 1);
}

:deep(.vue-tel-input:hover) {
  border: 1px solid #000;
}

:deep(.vue-tel-input:has(.vti__input:focus)) {
  transition: box-shadow 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  box-shadow: inset 0 0 0 1px #000; // Creates appearance of 2px border
}

:deep(.vti__input) {
  background: transparent;
  border: none;
  padding: 8px;
  font-size: 14px;
  outline: none;
  width: 100%;
}

:deep(.vti__dropdown) {
  padding: 4px 8px;
  background: transparent;
}

:deep(.vti__dropdown.open) {
  background: white;
}

:deep(.vti__dropdown-list) {
  border-radius: $generic-border-radius;
  border: 1px solid $grey-5;
}
</style>
